/**
 * Pure host add-to-cart outcome normalization and committed receipt builder.
 * Framework-free — shared by Chat and SimRel.
 */

import type { AddToCartHostOutcomeKind, AddToCartParams, HostEffectReceiptV1 } from './types.js';

export type NormalizedAddToCartHostResult = AddToCartHostOutcomeKind;

const PARTIAL_STATUSES = new Set(['navigated', 'host_flow_started']);
const UNCERTAIN_STATUSES = new Set(['possible_effect', 'uncertain']);
const COMMITTED_STATUS = 'ok';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeStatus(status: unknown): string | null {
  return typeof status === 'string' && status.trim() !== '' ? status.trim() : null;
}

function normalizeIdentity(value: unknown): string | null {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function matchesExpectedTarget(
  value: Record<string, unknown>,
  expected: AddToCartParams,
  requireAll: boolean,
): boolean {
  const rawCartCode = value['cartCode'];
  const rawCanonicalCartCode = value['cart_code'];
  const suppliedSku = value['sku'] !== undefined;
  const suppliedCartCode = rawCartCode !== undefined || rawCanonicalCartCode !== undefined;
  const suppliedQuantity = value['quantity'] !== undefined;

  if (requireAll && (!suppliedSku || !suppliedCartCode || !suppliedQuantity)) return false;

  const sku = suppliedSku ? normalizeIdentity(value['sku']) : expected.sku;
  const cartCode = rawCartCode === undefined ? expected.cartCode : normalizeIdentity(rawCartCode);
  const canonicalCartCode =
    rawCanonicalCartCode === undefined ? expected.cartCode : normalizeIdentity(rawCanonicalCartCode);
  const quantity = suppliedQuantity ? value['quantity'] : expected.quantity;

  return (
    sku === expected.sku &&
    cartCode === expected.cartCode &&
    canonicalCartCode === expected.cartCode &&
    typeof quantity === 'number' &&
    Number.isFinite(quantity) &&
    quantity === expected.quantity
  );
}

/**
 * Map an awaited `onAddToCart` return value to a canonical FE outcome.
 *
 * - `void` / `undefined` → committed (legacy hosts)
 * - `{ kind:'committed', sku, cartCode, quantity }` → committed when target-bound
 * - other valid `{ kind }` outcomes → that kind
 * - `{ ok:true, status:'ok' }` → committed when any supplied target fields match
 * - `{ ok:true, status:'navigated'|'host_flow_started' }` → partial
 * - `{ ok:true, status:'possible_effect'|'uncertain' }` → uncertain
 * - `{ ok:false }` / `status:'error'` → rejected
 * - unrecognized objects → uncertain (no success claim; no blind retry)
 */
export function normalizeAddToCartHostResult(value: unknown, expected: AddToCartParams): NormalizedAddToCartHostResult {
  if (value === undefined) {
    return 'committed';
  }

  if (!isRecord(value)) {
    return 'uncertain';
  }

  const kind = value['kind'];
  if (kind !== undefined && 'ok' in value) {
    return 'uncertain';
  }
  if (kind === 'committed') {
    return matchesExpectedTarget(value, expected, true) ? 'committed' : 'rejected';
  }
  if (kind === 'partial' || kind === 'uncertain') {
    return kind;
  }
  if (kind === 'rejected') {
    return 'rejected';
  }

  if (!('ok' in value)) {
    return 'uncertain';
  }

  const ok = value['ok'];
  const status = normalizeStatus(value['status']);

  if (ok !== true || status === 'error') {
    return 'rejected';
  }
  if (status === null) {
    return 'uncertain';
  }

  if (PARTIAL_STATUSES.has(status)) {
    return 'partial';
  }
  if (UNCERTAIN_STATUSES.has(status)) {
    return 'uncertain';
  }
  if (status === COMMITTED_STATUS) {
    return matchesExpectedTarget(value, expected, false) ? 'committed' : 'rejected';
  }

  // Unknown ok:true status — fail closed on success claims.
  return 'uncertain';
}

/** Build a v1 committed receipt bound to the exact FE sellable identity. */
export function buildCommittedHostEffectReceipt(params: AddToCartParams): HostEffectReceiptV1 {
  return {
    v: 1,
    effect: 'add_to_cart',
    outcome: 'committed',
    sku: params.sku,
    cart_code: params.cartCode,
    quantity: params.quantity,
  };
}
